import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Divider,
    TablePagination,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import {
    Visibility as ViewIcon,
    Email as EmailIcon
} from '@mui/icons-material';
import axios from 'axios';

function EmailHistory({ showAllOffers = false }) {
    const [offers, setOffers] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedOffer, setSelectedOffer] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalOffers, setTotalOffers] = useState(0);
    const [filters, setFilters] = useState({
        fromDate: '',
        toDate: '',
        agentId: '',
        search: ''
    });

    const fetchOffers = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            if (showAllOffers) {
                const response = await axios.get('/api/logs/offers-paginated', {
                    params: {
                        page: page + 1,
                        limit: rowsPerPage,
                        fromDate: filters.fromDate || undefined,
                        toDate: filters.toDate || undefined,
                        agentId: filters.agentId || undefined,
                        search: filters.search || undefined,
                        sort: 'sentAt_desc'
                    }
                });

                setOffers(response.data.rows || []);
                setTotalOffers(response.data.total || 0);
            } else {
                const response = await axios.get('/api/logs/my-offers');
                const userOffers = response.data.offers || [];
                setOffers(userOffers);
                setTotalOffers(userOffers.length);
            }
        } catch (error) {
            setError('Failed to fetch email history: ' + (error.response?.data?.error || error.message));
            setOffers([]);
            setTotalOffers(0);
        } finally {
            setLoading(false);
        }
    }, [showAllOffers, page, rowsPerPage, filters]);

    useEffect(() => {
        fetchOffers();
    }, [fetchOffers]);

    useEffect(() => {
        const fetchUsers = async () => {
            if (!showAllOffers) return;

            try {
                const response = await axios.get('/api/users');
                setUsers(response.data.users || []);
            } catch (error) {
                setError('Failed to load agent filter list: ' + (error.response?.data?.error || error.message));
            }
        };

        fetchUsers();
    }, [showAllOffers]);

    const handleViewDetails = async (offer) => {
        setDetailsOpen(true);
        setSelectedOffer(offer);
        setDetailsError('');

        if (!showAllOffers || offer.htmlContent) {
            return;
        }

        try {
            setDetailsLoading(true);
            const response = await axios.get(`/api/logs/offers/${offer.id}`);
            setSelectedOffer(response.data.offer);
        } catch (error) {
            setDetailsError('Failed to load email details: ' + (error.response?.data?.error || error.message));
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleCloseDetails = () => {
        setDetailsOpen(false);
        setSelectedOffer(null);
        setDetailsError('');
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
        setPage(0);
    };

    const clearFilters = () => {
        setFilters({
            fromDate: '',
            toDate: '',
            agentId: '',
            search: ''
        });
        setPage(0);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" mt={4}>
                <CircularProgress />
            </Box>
        );
    }

    const paginatedOffers = offers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                {showAllOffers ? 'All Email History' : 'My Email History'}
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
                {showAllOffers 
                    ? 'View all emails sent by all agents'
                    : 'View your sent email offers and their details'
                }
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {showAllOffers && (
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                label="From Date"
                                type="date"
                                value={filters.fromDate}
                                onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                label="To Date"
                                type="date"
                                value={filters.toDate}
                                onChange={(e) => handleFilterChange('toDate', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Agent</InputLabel>
                                <Select
                                    value={filters.agentId}
                                    onChange={(e) => handleFilterChange('agentId', e.target.value)}
                                    label="Agent"
                                >
                                    <MenuItem value="">All Agents</MenuItem>
                                    {users.filter(user => !user.isAdmin).map(user => (
                                        <MenuItem key={user.id} value={user.id}>
                                            {user.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="Search Customer"
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                placeholder="Name, email, phone, subject..."
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <Button variant="outlined" onClick={clearFilters} fullWidth>
                                Clear Filters
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {!error && offers.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <EmailIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        No emails sent yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {showAllOffers 
                            ? 'No agents have sent any offers yet.'
                            : 'Start sending offers to see your history here.'
                        }
                    </Typography>
                </Paper>
            ) : (
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    {showAllOffers && <TableCell>Agent</TableCell>}
                                    <TableCell>Customer</TableCell>
                                    <TableCell>Template</TableCell>
                                    <TableCell>Subject</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(showAllOffers ? offers : paginatedOffers).map((offer) => (
                                    <TableRow key={offer.id}>
                                        <TableCell>
                                            {new Date(offer.sentAt).toLocaleString()}
                                        </TableCell>
                                        {showAllOffers && (
                                            <TableCell>
                                                <Chip
                                                    label={offer.agentName || 'Unknown'}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {offer.customerFullName}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {offer.customerEmail}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={offer.templateName || 'Deleted Template'}
                                                size="small"
                                                color={offer.templateName ? 'primary' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                                {offer.subjectLine}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleViewDetails(offer)}
                                                title="View Details"
                                            >
                                                <ViewIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        component="div"
                        count={showAllOffers ? totalOffers : offers.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            )}

            {/* Email Details Dialog */}
            <Dialog 
                open={detailsOpen} 
                onClose={handleCloseDetails}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Email Details
                </DialogTitle>
                <DialogContent>
                    {selectedOffer && (
                        <Box>
                            {detailsLoading && (
                                <Box display="flex" alignItems="center" gap={1} mb={2}>
                                    <CircularProgress size={18} />
                                    <Typography variant="body2">Loading email details...</Typography>
                                </Box>
                            )}
                            {detailsError && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {detailsError}
                                </Alert>
                            )}
                            <Typography variant="h6" gutterBottom>
                                Email Information
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                                <Typography><strong>Sent:</strong> {new Date(selectedOffer.sentAt).toLocaleString()}</Typography>
                                {showAllOffers && (
                                    <Typography><strong>Agent:</strong> {selectedOffer.agentName}</Typography>
                                )}
                                <Typography><strong>Template:</strong> {selectedOffer.templateName || 'Deleted Template'}</Typography>
                                <Typography><strong>Customer:</strong> {selectedOffer.customerFullName}</Typography>
                                <Typography><strong>Email:</strong> {selectedOffer.customerEmail}</Typography>
                                {selectedOffer.customerPhone && (
                                    <Typography><strong>Phone:</strong> {selectedOffer.customerPhone}</Typography>
                                )}
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="h6" gutterBottom>
                                Subject Line
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2, p: 1, backgroundColor: 'grey.100', borderRadius: 1 }}>
                                {selectedOffer.subjectLine}
                            </Typography>

                            <Typography variant="h6" gutterBottom>
                                Email Content
                            </Typography>
                            <Box 
                                sx={{ 
                                    border: '1px solid #ccc', 
                                    p: 2, 
                                    maxHeight: 400,
                                    overflow: 'auto',
                                    backgroundColor: '#fff',
                                    borderRadius: 1
                                }}
                                dangerouslySetInnerHTML={{ __html: selectedOffer.htmlContent || (detailsLoading ? 'Loading content...' : 'Content not available') }}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDetails}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default EmailHistory;
